import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FormItemModel } from 'services/models/form-builder/form-item.model';
import { RecruitmentCandidateService } from 'services/recruitment/recruitment-candidate.service';
import { Subject, Observable } from 'rxjs';
import { takeUntil, finalize, switchMap, map, filter } from 'rxjs/operators';
import { HttpEventType } from '@angular/common/http';

@Component({
  selector: 'app-form-input-file',
  templateUrl: './form-input-file.component.html',
  styleUrls: ['./form-input-file.component.scss'],
})
export class FormInputFileComponent implements OnInit, OnDestroy {
  @Input() item!: FormItemModel;
  @Input() control!: FormControl;
  @Input() readonly: boolean = false;
  @Input() recruitmentId?: string;
  @Input() useAdmissionPreSignedUrl = false;

  uploadedFile: File | null = null;
  uploadedFileKey: string | null = null;
  isUploading = false;
  uploadProgress = 0;
  uploadError: string | null = null;

  private _unsubscribeAll = new Subject<void>();

  constructor(private recruitmentCandidateService: RecruitmentCandidateService) {}

  ngOnInit(): void {
    // Se já existe um fileKey no controle, inicializa o componente
    if (this.control?.value && typeof this.control.value === 'string') {
      this.uploadedFileKey = this.control.value;
      this.uploadProgress = 100;
    } else if (this.control?.value instanceof File) {
      this.uploadedFile = this.control.value;
      this.uploadProgress = 100;
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.readonly) return;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  private handleFile(file: File): void {
    // Validação de tipos permitidos
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];

    if (!allowedTypes.includes(file.type)) {
      this.uploadError = 'Por favor, selecione um arquivo PDF, PNG, JPEG ou JPG';
      return;
    }

    // Validação de tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      this.uploadError = 'O arquivo deve ter no máximo 5MB';
      return;
    }

    if (!this.recruitmentId) {
      this.uploadError = 'ID da vaga não encontrado';
      return;
    }

    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!guidRegex.test(this.recruitmentId)) {
      this.uploadError = 'ID da vaga inválido';
      return;
    }

    this.uploadError = null;
    this.uploadedFile = file;
    this.isUploading = true;
    this.uploadProgress = 0;

    // Faz upload imediatamente após selecionar o arquivo
    this.uploadFileToPreSignedUrl(file)
      .pipe(
        takeUntil(this._unsubscribeAll),
        finalize(() => {
          this.isUploading = false;
        })
      )
      .subscribe({
        next: result => {
          this.handleUploadEvent(result.event, result.fileKey);
        },
        error: error => {
          console.error('Erro no upload:', error);
          this.uploadError = 'Erro ao fazer upload do arquivo. Tente novamente.';
          this.uploadedFile = null;
          this.uploadProgress = 0;
          this.control.setValue(null);
        },
      });
  }

  private uploadFileToPreSignedUrl(file: File): Observable<{ event: any; fileKey: string }> {
    const preSigned$ = this.useAdmissionPreSignedUrl
      ? this.recruitmentCandidateService.getPreSignedUrlForAdmissionField(
          this.recruitmentId!,
          file.name,
          file.type
        )
      : this.recruitmentCandidateService.getPreSignedUrlForCustomField(
          this.recruitmentId!,
          file.name,
          file.type
        );

    return preSigned$.pipe(
        switchMap((data: { url: string; fileKey: string }) => {
          return this.recruitmentCandidateService.uploadToS3(data.url, file).pipe(
            map((event: any) => {
              if (event.type === HttpEventType.UploadProgress && event.total) {
                this.uploadProgress = Math.round((event.loaded / event.total) * 100);
              }
              return {
                event,
                fileKey: data.fileKey,
              };
            }),
            filter(
              (result: { event: any; fileKey: string }) =>
                result.event.type === HttpEventType.Response
            ),
            takeUntil(this._unsubscribeAll)
          );
        })
      );
  }

  private handleUploadEvent(event: any, fileKey: string): void {
    this.uploadProgress = 100;
    this.uploadedFileKey = fileKey;

    // Atualiza o FormControl com o fileKey
    this.control.setValue(fileKey);
    this.control.markAsTouched();
  }

  removeUploadedFile(): void {
    this.uploadedFile = null;
    this.uploadedFileKey = null;
    this.uploadProgress = 0;
    this.uploadError = null;
    this.control.setValue(null);
  }

  getFileIcon(file: File): string {
    if (file.type === 'application/pdf') {
      return 'fa-file-pdf text-danger';
    }
    return 'fa-file';
  }
}
